import { type PortfolioPowerRequest, resolvePortfolioAssets } from "./assetResolution";
import { type Portfolio, PortfolioDataSource } from "./types";

export type PortfolioPlotAsset = PortfolioPowerRequest & {
  label: string;
  model: "mix";
};

/** An Energy Forecast asset needs only its stable portfolio ID and Energy site ID. */
export type PortfolioEnergyForecastAsset = {
  assetId: string;
  label: string;
  siteId: string;
};

export type PortfolioPlotIssue = {
  assetId: string | null;
  code: "missing-asset-id" | "duplicate-asset-id" | "unavailable-asset" | "invalid-configuration";
  field?: string;
};

export type PortfolioPlotResolution =
  | { status: "unavailable" }
  | {
      status: "unsupported-source";
      dataSource: Exclude<Portfolio["dataSource"], PortfolioDataSource.Api | PortfolioDataSource.Energy>;
    }
  | {
      status: "resolved";
      dataSource: PortfolioDataSource.Api;
      assets: PortfolioPlotAsset[];
      issues: PortfolioPlotIssue[];
    }
  | {
      status: "resolved";
      dataSource: PortfolioDataSource.Energy;
      assets: PortfolioEnergyForecastAsset[];
      issues: PortfolioPlotIssue[];
    };

/** Return the stable IDs of every asset that can produce Portfolio Energy data. */
export function selectEligiblePortfolioAssetIds(portfolio: Portfolio | undefined): string[] {
  const assetIds = portfolio?.assets.flatMap((asset) => (asset.id ? [asset.id] : [])) ?? [];
  const resolution = resolvePortfolioPlotAssets(portfolio, assetIds);
  return resolution.status === "resolved" ? resolution.assets.map((asset) => asset.assetId) : [];
}

/** Resolve current detail rows into transient MW requests; never persist these descriptors. */
export function resolvePortfolioPlotAssets(
  portfolio: Portfolio | undefined,
  includedAssetIds: readonly string[],
): PortfolioPlotResolution {
  if (!portfolio) {
    return { status: "unavailable" };
  }
  if (portfolio.dataSource !== PortfolioDataSource.Api && portfolio.dataSource !== PortfolioDataSource.Energy) {
    return { status: "unsupported-source", dataSource: portfolio.dataSource };
  }

  const resolved = resolvePortfolioAssets(portfolio);
  const rows = new Map(resolved.flatMap((asset) => (asset.assetId ? [[asset.assetId, asset] as const] : [])));
  const issues: PortfolioPlotIssue[] = [];
  for (const asset of resolved) {
    if (asset.issue === "missing-asset-id") {
      issues.push({ assetId: null, code: "missing-asset-id" });
    }
  }

  const assets: PortfolioPlotAsset[] | PortfolioEnergyForecastAsset[] = [];
  for (const assetId of new Set(includedAssetIds)) {
    const asset = rows.get(assetId);
    if (!asset) {
      issues.push({ assetId, code: "unavailable-asset" });
      continue;
    }
    if (asset.issue === "duplicate-asset-id") {
      issues.push({ assetId, code: "duplicate-asset-id" });
      continue;
    }
    const invalidFields = asset.issue ? ["portfolioType"] : asset.invalidFields;
    if (invalidFields.length > 0) {
      issues.push(
        ...invalidFields.map((field): PortfolioPlotIssue => ({ assetId, code: "invalid-configuration", field })),
      );
      continue;
    }
    if (portfolio.dataSource === PortfolioDataSource.Api && asset.powerRequest) {
      (assets as PortfolioPlotAsset[]).push({
        ...asset.powerRequest,
        label: asset.label,
        model: "mix",
      });
    } else if (portfolio.dataSource === PortfolioDataSource.Energy && asset.siteId) {
      (assets as PortfolioEnergyForecastAsset[]).push({
        assetId,
        label: asset.label,
        siteId: asset.siteId,
      });
    }
  }
  return portfolio.dataSource === PortfolioDataSource.Api
    ? { status: "resolved", dataSource: PortfolioDataSource.Api, assets: assets as PortfolioPlotAsset[], issues }
    : {
        status: "resolved",
        dataSource: PortfolioDataSource.Energy,
        assets: assets as PortfolioEnergyForecastAsset[],
        issues,
      };
}
